import React from 'react';
import { I18n } from 'react-redux-i18n';
import isNil from 'lodash/isNil';
import { isUrl } from '@common/utils/url';
import { isEmail } from '@common/utils/email';
import isInteger from 'lodash/isInteger';
import { QuestionnaireUpdateLocalDto } from '../dto/questionnaire-update-local-dto';
import { QuestionnaireUpdateDto } from '@common/dto/questionnaire/update-dto';
import { QuestionnaireListCompactDto } from '@common/dto/questionnaire/list-compact-dto';
import { QuestionnairePageListDto } from '@common/dto/questionnaire/page/list-dto';
import { QuestionnairePageUpdateDto } from '@common/dto/questionnaire/page/update-dto';
import { QuestionnaireAutoReminderFrequencyType, QuestionnaireInactiveFrequencyType } from '@common/dto/questionnaire/frequency';
import { QuestionnaireCreateLocalDto } from '../dto/questionnaire-create-local-dto';
import { QuestionnaireCreateDto } from '@common/dto/questionnaire/create-dto';
import { QuestionnairePageType } from '@common/dto/questionnaire/page/type';
import { QuestionnaireFieldType } from '@common/dto/questionnaire/field/type';
import { QuestionnaireFieldUpdateDto } from '@common/dto/questionnaire/field/update-dto';
import { QuestionnaireFieldAddDto } from '@common/dto/questionnaire/field/add-dto';

type Errors = Array<{ text: string; }>;

export type QuestionnaireManagementPageErrorsProperties = {
  title: Errors;
};

export type QuestionnaireManagementPageNotesCustomErrors = {
  notesField: Errors;
};

export type QuestionnaireManagementPageCustomErrors = QuestionnaireManagementPageNotesCustomErrors;

type FieldId = string;

export type QuestionnaireManagementPageFieldsErrors = Record<FieldId, Errors>;

export type QuestionnaireManagementPageErrors = {
  custom?: QuestionnaireManagementPageCustomErrors;
  properties?: QuestionnaireManagementPageErrorsProperties;
  fields?: QuestionnaireManagementPageFieldsErrors;
};

export type QuestionnaireManagementErrorsProperties = {
  questionnaireName: Errors;
  genericLinkDefaultFolder: Errors;
  patientFirstName: Errors;
  patientLastName: Errors;
  patientEmail: Errors;
  exitUrl: Errors;
  externalUrl: Errors;
  logoUrl: Errors;
  sendEmailToStuffEmails: Errors;
  makeInactiveAfterFrequency: Errors;
  makeInactiveAfterType: Errors;
  makeInactiveAfterDateFieldName: Errors;
  submitTargetFolder: Errors;
  reviewedTargetFolder: Errors;
  inviteTargetFolder: Errors;
  emailNotifyOnSubmit: Errors;
  remindEveryValue: Errors;
  remindOnceValue: Errors;
  remindEveryType: Errors;
  remindEveryMax: Errors;
  remindOnceType: Errors;
  remindOnceDateFieldName: Errors;
};

export type QuestionnaireManagementErrorsPages = Record<string, QuestionnaireManagementPageErrors>;

export type QuestionnaireManagementErrors = {
  properties?: QuestionnaireManagementErrorsProperties;
  pages?: QuestionnaireManagementErrorsPages;
};

export const QUESTIONNAIRE_VALIDATION_CONSTANTS = {
  makeInactiveAfterFrequency: {
    min: 1,
    max: 999,
  },
};

export type ValidateQuestionnairePropertiesMetadata = {
  questionnaires: Array<QuestionnaireListCompactDto>;
};

export const useQuestionnaireValidationController = () => {
  const validateOptions = React.useCallback((field: QuestionnaireFieldUpdateDto | QuestionnaireFieldAddDto) => {
    const displayValues = (field.options || []).map((f) => f.displayValue);
    const duplicateDisplayValue = displayValues.find((value, index) => displayValues.indexOf(value) !== index);

    if (duplicateDisplayValue) {
      return [{ text: I18n.t('There is a duplicated display value') }];
    }
  }, []);

  const validatePageFields = React.useCallback((page: QuestionnairePageUpdateDto | QuestionnairePageListDto): QuestionnaireManagementPageFieldsErrors | undefined => {
    const errors: Record<FieldId, Errors> = {};
    const fields = page.fields || [];

    const fieldValidators: Partial<Record<QuestionnaireFieldType, (field: QuestionnaireFieldUpdateDto | QuestionnaireFieldAddDto) => Errors | void>> = {
      [QuestionnaireFieldType.dropdown]: validateOptions,
      [QuestionnaireFieldType.searchDropdown]: validateOptions,
      [QuestionnaireFieldType.radio]: validateOptions,
    };

    for (const field of fields) {
      const validator = fieldValidators[field.type];

      if (validator) {
        const fieldErrors = validator(field);

        if (fieldErrors) {
          errors[field.progenyId] = fieldErrors;
        }
      }
    }

    if (Object.keys(errors).length) {
      return errors;
    }
  }, []);

  const validatePages = React.useCallback((questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireUpdateDto | QuestionnaireCreateDto | QuestionnaireCreateLocalDto, metadata: ValidateQuestionnairePropertiesMetadata): QuestionnaireManagementErrorsPages => {
    const errors: Record<QuestionnairePageListDto['id'], QuestionnaireManagementPageErrors> = {};

    for (const page of questionnaire.designerTab.pages) {
      let pageErrors: QuestionnaireManagementPageErrors = {};

      const pageUpdateDto = page as QuestionnairePageUpdateDto | QuestionnairePageListDto;

      if (
        pageUpdateDto.pageType === QuestionnairePageType.finalNotes && 
        pageUpdateDto.enabled && 
        !pageUpdateDto.fields?.length
      ) {
        (pageErrors.custom as QuestionnaireManagementPageNotesCustomErrors) = {
          notesField: [{ text: I18n.t('The field is required') }],
        };
      }

      const pagePropertiesErrors: QuestionnaireManagementPageErrorsProperties = {
        title: [],
      };

      if (!(page as QuestionnairePageUpdateDto).title) {
        pagePropertiesErrors.title.push({ text: I18n.t('The field is required') });
      }

      if (Object.values(pagePropertiesErrors).some((x) => x.length > 0)) {
        pageErrors.properties = pagePropertiesErrors;
      }

      pageErrors.fields = validatePageFields(pageUpdateDto);

      if (pageErrors.custom || pageErrors.properties || pageErrors.fields) {
        errors[(page as QuestionnairePageUpdateDto).id] = pageErrors;
      }
    }

    return errors;
  }, []);


  const validateProperties = React.useCallback(async (questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireUpdateDto | QuestionnaireCreateDto | QuestionnaireCreateLocalDto, metadata: ValidateQuestionnairePropertiesMetadata) => {
    type FieldKey = keyof QuestionnaireManagementErrorsProperties;

    const isValidUrl = (url?: string) => {
      if (url) {
        return isUrl(url);
      }

      return true;
    };

    type ValidationMetadata = { text?: string; valid: boolean; };

    const validations: Record<
      FieldKey, 
      { getMetadata: () => Promise<ValidationMetadata> | ValidationMetadata }
    > = {
      questionnaireName: {
        getMetadata: () => {
          if (!questionnaire.questionnaireName) {
            return {
              text: I18n.t('The field is required'),
              valid: false
            }
          }

          const hasSameName = metadata.questionnaires.some((x) => {
            if (x.questionnaireId === questionnaire.questionnaireId) {
              return false;
            }

            return x.questionnaireName.toLowerCase().trim() === questionnaire.questionnaireName.toLowerCase().trim();
          });

          if (hasSameName) {
            return {
              text: I18n.t('Name is not unique'),
              valid: false
            }
          }

          return {
            valid: true,
          };
        },
      },
      genericLinkDefaultFolder: {
        getMetadata: () => {
          return {
            text: I18n.t('The field is required'),
            valid: !isNil(questionnaire.homeTab.genlinkTargetFolderId) && questionnaire.homeTab.genlinkTargetFolderId !== '' as any && questionnaire.homeTab.genlinkTargetFolderId !== 0
          }
        }
      },
      patientFirstName: {
        getMetadata: () => {
          return {
            text: I18n.t('The field is required'),
            valid: !isNil(questionnaire.homeTab.firstNameFieldId) && Boolean(questionnaire.homeTab.firstNameFieldName)
          }
        }
      },
      patientLastName: {
        getMetadata: () => {
          return {
            text: I18n.t('The field is required'),
            valid: !isNil(questionnaire.homeTab.lastNameFieldId) && Boolean(questionnaire.homeTab.lastNameFieldName)
          }
        }
      },
      patientEmail: {
        getMetadata: () => {
          return {
            text: I18n.t('The field is required'),
            valid: !isNil(questionnaire.homeTab.patientEmailFieldId) && Boolean(questionnaire.homeTab.patientEmailFieldName)
          }
        }
      },
      exitUrl: {
        getMetadata: () => {
          return {
            text: I18n.t('The field should be a valid URL'),
            valid: isValidUrl(questionnaire.homeTab.exitURL)
          }
        }
      },
      externalUrl: {
        getMetadata: () => {
          return {
            text: I18n.t('The field should be a valid URL'),
            valid: isValidUrl(questionnaire.homeTab.externalURL)
          }
        }
      },
      logoUrl: {
        getMetadata: () => {
          return {
            text: I18n.t('The field should be a valid URL'),
            valid: isValidUrl(questionnaire.homeTab.loginLogoURL)
          }
        }
      },
      sendEmailToStuffEmails: {
        getMetadata: () => {
          if (questionnaire.statusTab.sendEmailToStuffEmails) {
            const emails = questionnaire.statusTab.sendEmailToStuffEmails.split(/,\s*/);
            const nonEmails = emails.filter((val) => !isEmail(val));

            if (nonEmails.length) {
              return {
                text: `${I18n.t('There are not valid emails:')} ${nonEmails.join(', ')}`,
                valid: false,
              }
            }
          }

          return {
            valid: true,
          };
        }
      },
      submitTargetFolder: {
        getMetadata: () => {
          return {
            text: I18n.t('The field is required'),
            valid: questionnaire.statusTab.moveOnSubmit
              ? !isNil(questionnaire.statusTab.submitTargetFolderId) && questionnaire.statusTab.submitTargetFolderId !== '' as any && questionnaire.statusTab.submitTargetFolderId !== 0
              : true
          }
        }
      },
      reviewedTargetFolder: {
        getMetadata: () => {
          return {
            text: I18n.t('The field is required'),
            valid: questionnaire.statusTab.moveOnReviewed
              ? !isNil(questionnaire.statusTab.reviewedTargetFolderId) && questionnaire.statusTab.reviewedTargetFolderId !== '' as any && questionnaire.statusTab.reviewedTargetFolderId !== 0
              : true
          }
        }
      },
      inviteTargetFolder: {
        getMetadata: () => {
          return {
            text: I18n.t('The field is required'),
            valid: questionnaire.statusTab.moveOnInvite 
              ? !isNil(questionnaire.statusTab.inviteTargetFolderId) && questionnaire.statusTab.inviteTargetFolderId !== '' as any && questionnaire.statusTab.inviteTargetFolderId !== 0
              : true
          }
        }
      },
      emailNotifyOnSubmit: {
        getMetadata: () => {
          if (questionnaire.emailsTab.emailNotifyOnSubmit) {
            const emails = questionnaire.emailsTab.emailNotifyOnSubmit.split(/,\s*/);
            const nonEmails = emails.filter((val) => !isEmail(val));

            if (nonEmails.length) {
              return {
                text: `${I18n.t('There are not valid emails:')} ${nonEmails.join(', ')}`,
                valid: false,
              }
            }
          }

          return {
            valid: true,
          };
        }
      },
      makeInactiveAfterFrequency: {
        getMetadata: () => {
          if (!questionnaire.statusTab.makeUncompletedInactive || !questionnaire.statusTab.makeInactiveAfter) {
            return {
              valid: true,
            };
          }

          if (isNil(questionnaire.statusTab.makeInactiveAfterFrequency)) {
            return {
              valid: true,
            };
          }

          if (!isInteger(questionnaire.statusTab.makeInactiveAfterFrequency)) {
            return {
              text: I18n.t('The value should be an integer'),
              valid: false,
            };
          }

          if (
            questionnaire.statusTab.makeInactiveAfterFrequency < QUESTIONNAIRE_VALIDATION_CONSTANTS.makeInactiveAfterFrequency.min || 
            questionnaire.statusTab.makeInactiveAfterFrequency > QUESTIONNAIRE_VALIDATION_CONSTANTS.makeInactiveAfterFrequency.max
          ) {
            return {
              text: `${I18n.t('The value should be from')} ${QUESTIONNAIRE_VALIDATION_CONSTANTS.makeInactiveAfterFrequency.min} ${I18n.t('to')} ${QUESTIONNAIRE_VALIDATION_CONSTANTS.makeInactiveAfterFrequency.max}`,
              valid: false,
            };
          }

          return {
            valid: true,
          };
        }
      },
      makeInactiveAfterType: {
        getMetadata: () => {
          if (!questionnaire.statusTab.makeUncompletedInactive || !questionnaire.statusTab.makeInactiveAfter) {
            return {
              valid: true,
            };
          }

          return {
            text: I18n.t("The field is required"),
            valid: Object.values(QuestionnaireInactiveFrequencyType).includes(questionnaire.statusTab.makeInactiveAfterType as QuestionnaireInactiveFrequencyType),
          };
        }
      },
      makeInactiveAfterDateFieldName: {
        getMetadata: () => {
          if (!questionnaire.statusTab.makeUncompletedInactive || !questionnaire.statusTab.makeInactiveAfterDate) {
            return {
              valid: true,
            };
          }

          return {
            text: I18n.t("The field is required"),
            valid:
              !isNil(questionnaire.statusTab.makeInactiveAfterDateFieldName) &&
              questionnaire.statusTab.makeInactiveAfterDateFieldName.toString() !== "",
          };
        }
      },
      remindEveryValue: {
        getMetadata: () => {
          if (!questionnaire.emailsTab.enabledAutoReminders || !questionnaire.emailsTab.remindEvery) {
            return {
              valid: true,
            };
          }

          if (!isInteger(questionnaire.emailsTab.remindEveryValue)) {
            return {
              text: I18n.t('The value should be an integer'),
              valid: false,
            };
          }

          return {
            text: I18n.t('The value should be greater than 0'),
            valid: (questionnaire.emailsTab.remindEveryValue || 0) > 0
          }
        }
      },
      remindEveryType: {
        getMetadata: () => {
          if (!questionnaire.emailsTab.enabledAutoReminders || !questionnaire.emailsTab.remindEvery) {
            return {
              valid: true,
            };
          }

          return {
            text: I18n.t("The field is required"),
            valid: Object.values(QuestionnaireAutoReminderFrequencyType).includes(questionnaire.emailsTab.remindEveryType!),
          };
        }
      },
      remindEveryMax: {
        getMetadata: () => {
          if (!questionnaire.emailsTab.enabledAutoReminders || !questionnaire.emailsTab.remindEvery) {
            return {
              valid: true,
            };
          }

          if (!isInteger(questionnaire.emailsTab.remindEveryMax)) {
            return {
              text: I18n.t('The value should be an integer'),
              valid: false,
            };
          }

          return {
            text: I18n.t('The value should be greater than 0'),
            valid: (questionnaire.emailsTab.remindEveryMax || 0) > 0
          }
        }
      },
      remindOnceValue: {
        getMetadata: () => {
          if (!questionnaire.emailsTab.enabledAutoReminders || !questionnaire.emailsTab.remindOnce) {
            return {
              valid: true,
            };
          }

          if (!isInteger(questionnaire.emailsTab.remindOnceValue)) {
            return {
              text: I18n.t('The value should be an integer'),
              valid: false,
            };
          }

          return {
            text: I18n.t('The value should be greater than 0'),
            valid: (questionnaire.emailsTab.remindOnceValue || 0) > 0
          }
        }
      },
      remindOnceType: {
        getMetadata: () => {
          if (!questionnaire.emailsTab.enabledAutoReminders || !questionnaire.emailsTab.remindOnce) {
            return {
              valid: true,
            };
          }

          return {
            text: I18n.t("The field is required"),
            valid: Object.values(QuestionnaireAutoReminderFrequencyType).includes(questionnaire.emailsTab.remindOnceType!),
          };
        }
      },
      remindOnceDateFieldName: {
        getMetadata: () => {
          if (!questionnaire.emailsTab.enabledAutoReminders || !questionnaire.emailsTab.remindOnce) {
            return {
              valid: true,
            };
          }

          return {
            text: I18n.t("The field is required"),
            valid:
              !isNil(questionnaire.emailsTab.remindOnceDateFieldName) &&
              questionnaire.emailsTab.remindOnceDateFieldName.toString() !== "",
          };
        }
      },
    };

    const propertiesErrors: QuestionnaireManagementErrorsProperties = {
      questionnaireName: [],
      genericLinkDefaultFolder: [],
      exitUrl: [],
      externalUrl: [],
      logoUrl: [],
      patientFirstName: [],
      patientLastName: [],
      patientEmail: [],
      sendEmailToStuffEmails: [],
      emailNotifyOnSubmit: [],
      submitTargetFolder: [],
      reviewedTargetFolder: [],
      inviteTargetFolder: [],
      makeInactiveAfterFrequency: [],
      remindEveryValue: [],
      remindOnceValue: [],
      remindEveryType: [],
      remindOnceType: [],
      remindEveryMax: [],
      remindOnceDateFieldName: [],
      makeInactiveAfterType: [],
      makeInactiveAfterDateFieldName: [],
    };

    const validationsRequests: Array<Promise<void>> = [];
    
    Object.keys(validations).forEach((key) => {
      const fieldKey = key as FieldKey;
      const fieldValidation = validations[fieldKey];

      const request = Promise.resolve(fieldValidation.getMetadata()).then((response) => {
        if (!response.valid && response.text) {
          propertiesErrors[fieldKey].push({ text: response.text });
        }
      });

      validationsRequests.push(request);
    });

    await Promise.all(validationsRequests);

    return propertiesErrors;
  }, []);

  const validateQuestionnaire = React.useCallback(async (
    questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireUpdateDto | QuestionnaireCreateDto | QuestionnaireCreateLocalDto, 
    metadata: ValidateQuestionnairePropertiesMetadata
  ) => {
    const propertiesErrors = await validateProperties(questionnaire, metadata);
    const pagesErrors = validatePages(questionnaire, metadata);

    const propertiesHaveError = Object.values(propertiesErrors).some((x) => x.length > 0);
    const pagesHaveError = Object.values(pagesErrors).some((x) => {
      const propertiesErrorsResult = Object.values(x.properties || {}).some((y) => y.length > 0);
      const customErrorsResult = Object.values(x.custom || {}).some((y) => y.length > 0);
      const fieldsErrorsResult = Object.values(x.fields || {}).some((y) => y.length > 0);

      return propertiesErrorsResult || customErrorsResult || fieldsErrorsResult;
    });

    const errors: QuestionnaireManagementErrors = {
      properties: propertiesHaveError ? propertiesErrors : undefined,
      pages: pagesHaveError ? pagesErrors : undefined
    };

    return (pagesHaveError || propertiesHaveError) ? errors : undefined;
  }, []);

  return {
    validateQuestionnaire,
  };
};